import {Injectable} from '@angular/core';
import {Observable, timer, throwError, of} from 'rxjs';
import {
  switchMap,
  map,
  filter,
  take,
  timeout,
  catchError,
} from 'rxjs/operators';
import {BasketService} from '@app/providers/basket.service';
import {OloPayTerminalService} from './olo-pay-terminal.service';
import {reportHandledError} from '@app/core/sentry/sentry-report';
import {
  GetPaymentSessionStatusResponse,
  PaymentSessionStatus,
} from '@app/models/olo.get-payment-session-status-response';

export interface OloPayResult {
  status: string;
  paymentSessionId: string;
  billingSchemeId: string;
  cardNumber?: string;
  cardType?: string;
  authCode?: string;
  refNo?: string;
}

@Injectable({
  providedIn: 'root',
})
export class OloPayPaymentService {
  constructor(
    private basketService: BasketService,
    private oloPayTerminal: OloPayTerminalService
  ) {}

  /**
   * Process a credit/debit card payment using OloPay payment session.
   *
   * Preflights `ensureNoActivePaymentSession` to cancel any stale session left on the
   * terminal by a prior order whose submit failed or whose session was never finalized.
   * This closes the "Cause B" path in openspec/changes/fix-terminal-residual-state where
   * Order 2's customer could otherwise be presented with Order 1's authorize-for amount.
   * The probe is a cheap HTTP round-trip on a healthy terminal.
   */
  processCardPayment(
    basketId: string,
    billingSchemeId: string,
    amount: number
  ): Observable<OloPayResult> {
    const terminalId = this.oloPayTerminal.pinPadMACAddress;

    if (!terminalId) {
      return throwError(() => new Error('Terminal ID not configured'));
    }

    // Preflight: cancel any stale session, then create a fresh session for the current basket.
    return this.ensureNoActivePaymentSession(basketId, billingSchemeId).pipe(
      switchMap(() =>
        this.basketService.createPaymentSession(
          basketId,
          billingSchemeId,
          terminalId,
          amount
        )
      ),
      switchMap((sessionResponse) => {
        // Poll for payment status until complete
        return this.pollPaymentSessionStatus(
          basketId,
          billingSchemeId,
          sessionResponse.id
        ).pipe(
          map((statusResponse) => {
            if (statusResponse.status === PaymentSessionStatus.APPROVED) {
              const cust = statusResponse.receiptdata?.Customer ?? [];
              const get = (k: string) =>
                cust.find((i) => i.Key === k)?.Value || '';

              const cardNumber = get('pan') || 'XXXXXXXXXXXX';
              const cardType = get('paymentMethod') || 'Credit';
              const authCode = get('authCode') || 'AUTH';
              const refNo = get('txRef') || sessionResponse.id;

              return {
                status: statusResponse.status,
                paymentSessionId: sessionResponse.id,
                billingSchemeId: billingSchemeId,
                cardNumber,
                cardType,
                authCode,
                refNo,
              };
            }

            return {
              status: statusResponse.status,
              paymentSessionId: sessionResponse.id,
              billingSchemeId: billingSchemeId,
            };
          })
        );
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Cancel a payment session
   */
  cancelPaymentSession(
    basketId: string,
    billingSchemeId: string,
    paymentSessionId: string
  ): Observable<void> {
    return this.basketService
      .cancelAPaymentSession(basketId, billingSchemeId, paymentSessionId)
      .pipe(
        map((): void => void 0),
        catchError((error): Observable<void> => {
          console.error('Error cancelling payment session:', error);
          return of(void 0);
        })
      );
  }

  /**
   * Check if there's currently an active payment session on the terminal.
   *
   * Returns the stale session ID (if the terminal is busy) or null (if it's free).
   * The caller MUST treat any returned session ID as a session-to-cancel, never a
   * session-to-resume — the Olo payment session is an Olo REST construct keyed to
   * `basketId` + `billingSchemeId` at create-time, and resuming a session from a
   * previous basket against the current basket would authorize the wrong amount.
   * See openspec/changes/fix-terminal-residual-state for the invariant.
   */
  checkForActivePaymentSession(
    basketId: string,
    billingSchemeId: string
  ): Observable<string | null> {
    const terminalId = this.oloPayTerminal.pinPadMACAddress;

    // Try to create a test payment session with amount 0 to check if terminal is busy
    return this.basketService
      .createPaymentSession(basketId, billingSchemeId, terminalId, 0)
      .pipe(
        switchMap((response): Observable<string | null> => {
          // If we got a session response, immediately cancel it since it was just a test
          return this.cancelPaymentSession(
            basketId,
            billingSchemeId,
            response.id
          ).pipe(
            map((): string | null => null) // No active session found
          );
        }),
        catchError((error): Observable<string | null> => {
          // Check if error indicates terminal is busy with existing session
          if (
            error.status === 409 ||
            error.error?.message?.includes('session') ||
            error.error?.message?.includes('busy')
          ) {
            // Terminal is busy, try to get existing session ID from error
            const existingSessionId = this.extractSessionIdFromError(error);
            return of(existingSessionId);
          }
          // Other error - assume no active session
          return of(null);
        })
      );
  }

  /**
   * Ensure no active payment session exists before starting a new one
   */
  ensureNoActivePaymentSession(
    basketId: string,
    billingSchemeId: string
  ): Observable<void> {
    return this.checkForActivePaymentSession(basketId, billingSchemeId).pipe(
      switchMap((existingSessionId): Observable<void> => {
        if (existingSessionId) {
          // Cancel existing session
          return this.cancelPaymentSession(
            basketId,
            billingSchemeId,
            existingSessionId
          ).pipe(
            catchError((error): Observable<void> => {
              // Continuing anyway is the right call for the customer standing at
              // the terminal, but a stale session we could not cancel is the
              // start of a stuck-terminal report from the store — so record it
              // instead of only waiting 2 s and moving on.
              reportHandledError(error, {
                source: 'olo-pay-stale-session-cancel-failed',
                extra: {basketId, billingSchemeId, existingSessionId},
              });
              // If cancel fails, wait a moment and continue anyway
              return timer(2000).pipe(map((): void => void 0));
            })
          );
        } else {
          // No existing session
          return of(void 0);
        }
      })
    );
  }

  /**
   * Poll payment session status for credit/debit card payments
   */
  private pollPaymentSessionStatus(
    basketId: string,
    billingSchemeId: string,
    paymentSessionId: string
  ): Observable<GetPaymentSessionStatusResponse> {
    return timer(3000, 3000).pipe(
      // First poll after 3 seconds, then every 3 seconds
      switchMap(() =>
        this.basketService.getStatusOfPaymentSession(
          basketId,
          billingSchemeId,
          paymentSessionId
        )
      ),
      map((response) => {
        // If status is InProgress, continue polling
        if (response.status === PaymentSessionStatus.IN_PROGRESS) {
          return null;
        }
        // Otherwise return the final status
        return response;
      }),
      filter((response) => response !== null),
      take(1),
      timeout(120000) // 2 minute timeout
    );
  }

  /**
   * Attempt to extract session ID from error response
   */
  private extractSessionIdFromError(error: any): string | null {
    try {
      // Try to extract session ID from error message or response
      const errorMessage = error.error?.message || error.message || '';
      const sessionIdMatch = errorMessage.match(
        /session[^a-zA-Z0-9]*([a-zA-Z0-9-]{20,})/i
      );
      return sessionIdMatch ? sessionIdMatch[1] : null;
    } catch {
      return null;
    }
  }
}
